import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardStats } from './components/DashboardStats';
import Patients from './components/Patients';
import Users from './components/Users';
import Settings from './components/Settings';
import Organizations from './components/Organizations';
import Programs from './components/Programs';
import Groups from './components/Groups';
import { FormManager } from './components/PatientDetail/intake-forms/FormManager';
import { EventDetailsModal, PatientSearchModal, BlockedUserPopup, Sidebar, MobileHeader, LoadingScreen, CalendarSection, TasksSection, AppBar } from './components/dashboard';
import { signOutUser, getUserData, syncEventToGoogleCalendar } from './firebase';
import { User } from './types';
import { PatientNameDisplay, PatientNotesEditable } from './components/PatientComponents';
import { formatDate } from './utils';
import { useLanguageNavigate, useEvents, usePatients, useUsers, useMilestones, useTasksAndNotifications, useActivityLogs, useDashboardStats, useGoogleIntegration } from './hooks';
import { useCustomDialog } from './components/CustomDialog';
import { AIChatButton } from './components/AIChatButton';
import { 
  canSeeUsersTab, 
  canSeeOrganizationsTab, 
  canSeeFormsTab,
  canSeeProgramsTab,
  canSeeGroupsTab,
  isOrgAdmin,
  canSeeSitemap
} from './utils/roleUtils';

// Main Dashboard Component (Application Layout)
export default function MainDashboard() {
  const navigate = useNavigate();
  const langNavigate = useLanguageNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { showAlert, showConfirm, DialogComponent } = useCustomDialog();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>('');
  
  // Auto-close mobile menu when window resizes to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);
  
  // Auto-close mobile menu when route/location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Auto-close mobile menu when clicking outside (on mobile)
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sidebar = document.querySelector('.sidebar');
      const overlay = document.querySelector('.mobile-menu-overlay');
      
      // Check if click is outside sidebar and not on overlay
      if (sidebar && !sidebar.contains(target) && window.innerWidth <= 768) {
        // If clicking on overlay or main content, close menu
        if (overlay?.contains(target) || target.closest('.main-content')) {
          setIsMobileMenuOpen(false);
        }
      }
    };
    
    // Add listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);
  
  // Function to handle tab change and close mobile menu
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    // Clear search when switching tabs
    setGlobalSearchTerm('');
  };

  // Handle global search - only updates search term
  const handleGlobalSearchChange = (term: string) => {
    setGlobalSearchTerm(term);
  };

  // Function to handle patient selection
  const handlePatientSelect = (caseId: string, logId?: string) => {
    console.log('Navigating to patient detail for caseId:', caseId);
    
    // Validate that the case ID exists in the current patient list
    const patientExists = filteredPatients.some(patient => patient.caseId === caseId);
    
    if (!patientExists) {
      console.error('Patient not found in current list:', caseId);
      // If logId is provided, automatically delete the activity log entry
      if (logId) {
        console.log('Auto-deleting activity log for non-existent case:', logId);
        handleDeleteActivityLog(logId);
      } else {
        // Show confirmation dialog with delete option
        showConfirm(
          `Patient ${caseId} not found. Would you like to delete this case?`,
          async () => {
            const result = await deletePatient(caseId);
            if (result.success) {
              showAlert(`Case ${caseId} has been deleted successfully.`);
            } else {
              showAlert(`Failed to delete case ${caseId}. Please try again.`);
            }
          },
          'Patient Not Found',
          'Delete Case',
          'Cancel'
        );
      }
      return;
    }
    
    // Navigate to the patient detail page with navbar visible
    langNavigate(`/patient/${caseId}`);
  };

  
  // Use custom hooks for state management
  const {
    events,
    setEvents,
    isEventsLoading,
    handleEventStatusChange,
    handleDeleteEvent
  } = useEvents(user);

  const {
    patients,
    filteredPatients,
    isPatientLoading,
    patientManagementSearchTerm,
    patientStatusFilter,
    handlePatientStatusChange,
    handleDeletePatient: deletePatient,
    handlePatientManagementSearch,
    handlePatientStatusFilterChange,
    handleFilterByUser,
    refreshPatients
  } = usePatients(user);

  const {
    users,
    filteredUsers,
    userSearchTerm,
    userStatusFilter,
    userIntakeCompletions,
    handleUserSearchChange,
    handleUserStatusFilterChange,
    handleDeleteUser: deleteUserHook,
    handleUserStatusChange,
    refreshUsers
  } = useUsers(user, patients);

  const {
    todayMilestones,
    isTodayMilestonesLoading,
    loadAllMilestones,
    handleDeleteMilestone,
    handleUpdateMilestoneStatus
  } = useMilestones(user, patients, showAlert);

  const {
    todayTasks,
    pendingTasks,
    allTasks,
    notifications,
    isTasksLoading,
    isNotificationsLoading,
    handleTaskStatusChange,
    handleMarkNotificationRead,
    handleDeleteTask,
    handleDeleteNotification,
    refreshTasks
  } = useTasksAndNotifications(user, i18n.language);

  const {
    activityLogs,
    setActivityLogs,
    isActivityLoading,
    activitySearchTerm,
    setActivitySearchTerm,
    activityTimeFilter,
    setActivityTimeFilter,
    activityCurrentPage,
    setActivityCurrentPage,
    activitiesPerPage,
    handleDeleteActivityLog,
    getFilteredActivityLogs,
    getPaginatedActivityLogs
  } = useActivityLogs(user, showAlert);

  const {
    dashboardStats,
    isStatsLoading,
    inactivePatients
  } = useDashboardStats(user, events, loadAllMilestones, setActivityLogs);

  // Helper function for refreshing user data
  const refreshUserData = async (userId: string) => {
    try {
      console.log('Refreshing user data for userId:', userId);
      const userDataFromDB = await getUserData(userId);
      console.log('User data from Firestore:', userDataFromDB);
      
      if (userDataFromDB) {
        const updatedUserData: User = {
          id: userId,
          name: userDataFromDB.name,
          email: userDataFromDB.email,
          picture: userDataFromDB.picture || `https://via.placeholder.com/80x80/007acc/ffffff?text=${userDataFromDB.name.charAt(0).toUpperCase()}`,
          role: userDataFromDB.role,
          organizationId: userDataFromDB.organizationId,
          organizationRole: userDataFromDB.organizationRole,
          legacyRole: userDataFromDB.legacyRole,
          createdAt: userDataFromDB.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          lastLoginAt: userDataFromDB.lastLoginAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          loginCount: userDataFromDB.loginCount || 1,
          hasPiiData: userDataFromDB.hasPiiData || false,
          hasGoogleAccount: userDataFromDB.hasGoogleAccount || false,
          hasGoogleCalendar: userDataFromDB.hasGoogleCalendar || false,
          blocked: userDataFromDB.blocked || false,
          blockedReason: userDataFromDB.blockedReason || '',
        };
        
        setUser(updatedUserData);
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        console.log('User data updated successfully');
      } else {
        console.log('No user data found in Firestore');
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  };

  const {
    isLinkingGoogle,
    isLinkingCalendar,
    handleLinkGoogleAccount,
    handleLinkGoogleCalendar,
    handleUnlinkGoogleCalendar
  } = useGoogleIntegration(user, refreshUserData, showAlert, showConfirm);

  // Remaining state variables
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState<'calendar' | 'list'>('calendar');
  // Removed archive functionality - only show active/new events
  
  // Date range: default is 1 week back to 1 week forward
  const getDefaultDateRange = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAhead = new Date(today);
    weekAhead.setDate(weekAhead.getDate() + 7);
    
    return {
      from: weekAgo.toISOString().split('T')[0],
      to: weekAhead.toISOString().split('T')[0]
    };
  };
  
  const [dateRangeFilter, setDateRangeFilter] = useState(getDefaultDateRange());
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEventForDetails, setSelectedEventForDetails] = useState<any>(null);
  const [editedEventData, setEditedEventData] = useState<any>(null);
  const [isEventSaving, setIsEventSaving] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState<string | null>(null);
  const [showPatientMenu, setShowPatientMenu] = useState<string | null>(null);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(10);

  // Sync eventSearchTerm with globalSearchTerm when on calendar tab
  useEffect(() => {
    if (activeTab === 'calendar' && eventSearchTerm !== globalSearchTerm) {
      setEventSearchTerm(globalSearchTerm);
    }
  }, [activeTab, globalSearchTerm]);
  const [showPatientSearchModal, setShowPatientSearchModal] = useState(false);
  const [patientCurrentPage, setPatientCurrentPage] = useState(1);
  const [patientsPerPage] = useState(10);
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [selectedPatientForEvent, setSelectedPatientForEvent] = useState<any>(null);

  // Calendar functionality
  const getCalendarDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  }, [currentDate]);

  const getEventsForDate = useCallback((date: Date) => {
    let filtered = events.filter(event => {
      if (!event.date) return false;
      const eventDate = event.date.toDate ? event.date.toDate() : new Date(event.date);
      
      // Compare dates using local date strings to avoid timezone issues
      const eventYear = eventDate.getFullYear();
      const eventMonth = eventDate.getMonth();
      const eventDay = eventDate.getDate();
      
      const selectedYear = date.getFullYear();
      const selectedMonth = date.getMonth();
      const selectedDay = date.getDate();
      
      return eventYear === selectedYear && eventMonth === selectedMonth && eventDay === selectedDay;
    });
    
    // Apply role-based filtering: super admins see all, regular admins see only their own
    if (user?.role !== 'super_admin') {
      filtered = filtered.filter(event => event.createdBy === user?.id);
    }
    
    // Filter to show only newly created events (status 'new' or 'active')
    filtered = filtered.filter(event => {
      // Exclude archived events
      if (event.archived === true) {
        return false;
      }
      // Only show newly created events (status 'new' or 'active')
      const status = event.status?.toLowerCase();
      return status === 'new' || status === 'active';
    });
    
    return filtered;
  }, [events, user?.role, user?.id]);

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  }, []);

  const getFilteredEvents = useCallback(() => {
    let filtered = events;
    
    // Automatically filter by user role
    // Super admins see all events, regular admins see only their own events
    if (user?.role !== 'super_admin') {
      filtered = filtered.filter(event => event.createdBy === user?.id);
    }
    
    // Only show newly created events (status 'new' or 'active')
      filtered = filtered.filter(event => {
      // Exclude archived events
        if (event.archived === true) {
          return false;
        }
        const status = event.status?.toLowerCase();
      // Only show newly created events
      return status === 'new' || status === 'active';
      });
    
    // Filter by date range
    if (dateRangeFilter.from || dateRangeFilter.to) {
      filtered = filtered.filter(event => {
        if (!event.date) return false;
        const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
        
        // Use local date string to avoid timezone issues
        const year = eventDate.getFullYear();
        const month = String(eventDate.getMonth() + 1).padStart(2, '0');
        const day = String(eventDate.getDate()).padStart(2, '0');
        const eventDateString = `${year}-${month}-${day}`;
        
        const afterFrom = !dateRangeFilter.from || eventDateString >= dateRangeFilter.from;
        const beforeTo = !dateRangeFilter.to || eventDateString <= dateRangeFilter.to;
        
        return afterFrom && beforeTo;
      });
    }
    
    // Filter by search term
    if (eventSearchTerm) {
      const searchLower = eventSearchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.title?.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.caseId?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [events, dateRangeFilter, eventSearchTerm, user?.id, user?.role]);

  const getPaginatedEvents = useCallback(() => {
    const filtered = getFilteredEvents();
    const startIndex = (currentPage - 1) * eventsPerPage;
    return filtered.slice(startIndex, startIndex + eventsPerPage);
  }, [getFilteredEvents, currentPage, eventsPerPage]);

  // Event handlers
  const openEventDetailsModal = useCallback((event: any) => {
    setSelectedEventForDetails(event);
    setShowEventDetailsModal(true);
  }, []);

  const handleSyncSingleEvent = useCallback(async (event: any) => {
    try {
      await syncEventToGoogleCalendar(event);
      // Events will be refreshed automatically by the useEvents hook
    } catch (error) {
      console.error('Error syncing event:', error);
    }
  }, []);

  // Handler functions that are NOT in hooks
  const handleSelectPatientForEvent = useCallback((patient: any) => {
    setSelectedPatientForEvent(patient);
    setShowCreateEventModal(false);
    // Navigate to create event with patient data in state
    langNavigate('/create-event', { 
      state: { 
        selectedPatient: patient 
      } 
    });
  }, [langNavigate]);


  const switchToListView = useCallback((date: Date) => {
    // Set the selected date range to only the selected day
    // Use local date to avoid timezone issues
    
    // Selected date only
    const selectedYear = date.getFullYear();
    const selectedMonth = String(date.getMonth() + 1).padStart(2, '0');
    const selectedDay = String(date.getDate()).padStart(2, '0');
    const selectedDateString = `${selectedYear}-${selectedMonth}-${selectedDay}`;
    
    // Set date range to only the selected date
    setDateRangeFilter({ from: selectedDateString, to: selectedDateString });
    // Switch to list view
    setCalendarViewMode('list');
    // Set selected date for highlighting
    setSelectedDate(date);
  }, []);

  const handleDeletePatientWrapper = useCallback(async (caseId: string) => {
    showConfirm('Are you sure you want to delete this patient? This action cannot be undone.', async () => {
      const result = await deletePatient(caseId);
      if (!result.success) {
        showAlert('Failed to delete patient');
      }
    });
  }, [deletePatient, showConfirm, showAlert]);

  const handleDeleteUserWrapper = useCallback(async (userId: string) => {
    showConfirm(t('editUser.confirmDeleteUser'), async () => {
      const result = await deleteUserHook(userId);
      if (!result.success) {
        showAlert('Failed to delete user. Please try again.');
      }
    });
  }, [deleteUserHook, showConfirm, showAlert, t]);

  const getPaginatedPatients = useCallback(() => {
    const startIndex = (patientCurrentPage - 1) * patientsPerPage;
    return filteredPatients.slice(startIndex, startIndex + patientsPerPage);
  }, [filteredPatients, patientCurrentPage, patientsPerPage]);

  const getPaginatedUsers = useCallback(() => {
    const startIndex = (userCurrentPage - 1) * usersPerPage;
    return filteredUsers.slice(startIndex, startIndex + usersPerPage);
  }, [filteredUsers, userCurrentPage, usersPerPage]);

  const handleSignOut = async (skipConfirm?: boolean) => {
    const doSignOut = async () => {
      try {
        await signOutUser();
        setUser(null);
        localStorage.removeItem('user');
        navigate('/');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    };
    if (skipConfirm) {
      await doSignOut();
      return;
    }
    showConfirm(t('navigation.confirmSignOut'), doSignOut);
  };

  // Check for existing user session on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Loaded user from localStorage:', userData);
        setUser(userData);
        refreshUserData(userData.id);
      } catch (err) {
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  // Set default tab based on user role - removed redirect, all users can access dashboard now
  useEffect(() => {
    if (user) {
      // All users can now access dashboard
    }
  }, [user, activeTab]);

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const searchParam = searchParams.get('q');
    
    // Restore search term from URL if present
    if (searchParam) {
      setGlobalSearchTerm(searchParam);
    }
    
    const validTabs = [
      'dashboard', 
      'projects', 
      'calendar', 
      'users', 
      'settings', 
      'forms',
      'organizations',
      'programs',
      'groups',
      'members'
    ];
    if (tabParam && validTabs.includes(tabParam)) {
      console.log('Setting active tab from URL parameter:', tabParam);
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Initialize language and RTL direction
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const currentLanguage = savedLanguage || i18n.language;
    
    document.documentElement.dir = currentLanguage === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
    
    if (currentLanguage !== i18n.language) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [i18n]);

  // Check if user is blocked and show popup
  useEffect(() => {
    if (user && user.blocked) {
      setShowBlockedPopup(true);
    } else {
      setShowBlockedPopup(false);
    }
  }, [user]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    console.log('No user found in Dashboard, redirecting to login');
    return <Navigate to="/" replace />;
  }

  console.log('Dashboard rendering with user:', user.email, 'role:', user.role, 'activeTab:', activeTab);

  return (
    <>
      <div className="app dashboard-mode">
        <MobileHeader
          user={user}
          isMobileMenuOpen={isMobileMenuOpen}
          onToggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />

        <div className="dashboard-container">
          <div className="dashboard">
            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <div 
                className="mobile-menu-overlay"
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
            )}
            
            <Sidebar
              user={user}
              activeTab={activeTab}
              isMobileMenuOpen={isMobileMenuOpen}
              onTabChange={handleTabChange}
              onSignOut={() => handleSignOut()}
              onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Global AppBar with Search - Available on all tabs */}
            <AppBar
              searchTerm={globalSearchTerm}
              onSearchChange={handleGlobalSearchChange}
              activeTab={activeTab}
              user={user}
              onUserClick={() => handleTabChange('settings')}
              onSettingsClick={() => handleTabChange('settings')}
            />
            
            <div className="main-content">
              {activeTab === 'dashboard' && (
                <div className="dashboard-content">
                  {/* Use existing DashboardStats component with milestones integrated */}
                  <DashboardStats
                    user={user}
                    dashboardStats={dashboardStats}
                    isStatsLoading={isStatsLoading}
                    activityLogs={getPaginatedActivityLogs()}
                    totalActivityLogs={getFilteredActivityLogs().length}
                    isActivityLoading={isActivityLoading}
                    activitySearchTerm={activitySearchTerm}
                    setActivitySearchTerm={setActivitySearchTerm}
                    activityTimeFilter={activityTimeFilter}
                    setActivityTimeFilter={setActivityTimeFilter}
                    activityCurrentPage={activityCurrentPage}
                    setActivityCurrentPage={setActivityCurrentPage}
                    activityTotalPages={Math.ceil(getFilteredActivityLogs().length / activitiesPerPage)}
                    activitiesPerPage={activitiesPerPage}
                    todayMilestones={todayMilestones}
                    isTodayMilestonesLoading={isTodayMilestonesLoading}
                    handleDeleteMilestone={handleDeleteMilestone}
                    handlePatientSelect={handlePatientSelect}
                    handleDeleteActivityLog={handleDeleteActivityLog}
                    i18n={i18n}
                    t={t}
                    // New props for tasks and notifications
                    todayTasks={todayTasks}
                    pendingTasks={pendingTasks}
                    allTasks={allTasks}
                    notifications={notifications}
                    inactivePatients={inactivePatients}
                    isTasksLoading={isTasksLoading}
                    isNotificationsLoading={isNotificationsLoading}
                    handleTaskStatusChange={handleTaskStatusChange}
                    handleMarkNotificationRead={handleMarkNotificationRead}
                    handleDeleteTask={handleDeleteTask}
                    handleDeleteNotification={handleDeleteNotification}
                    handleDeletePatient={handleDeletePatientWrapper}
                    refreshTasks={refreshTasks}
                    // Events props
                    events={events}
                    isEventsLoading={isEventsLoading}
                    // Widget click handler
                    onWidgetClick={(widgetId: string, tab: string, filter?: any) => {
                      // Apply filter if provided
                      if (filter) {
                        if (filter.patientStatusFilter) {
                          handlePatientStatusFilterChange(filter.patientStatusFilter);
                        }
                        if (filter.showUpcoming) {
                          // Set date range to show upcoming events (from today onwards)
                          const today = new Date().toISOString().split('T')[0];
                          setDateRangeFilter({ from: today, to: '' });
                        }
                        if (filter.taskFilter) {
                          // Task filters can be handled in the dashboard view
                          // This could scroll to or highlight the relevant task section
                        }
                      }
                      // Switch to the tab
                      handleTabChange(tab);
                    }}
                  />
                </div>
              )}

              {activeTab === 'settings' && (
                <Settings
                  user={user}
                  i18n={i18n}
                  isLinkingCalendar={isLinkingCalendar}
                  handleLinkGoogleCalendar={handleLinkGoogleCalendar}
                  handleUnlinkGoogleCalendar={handleUnlinkGoogleCalendar}
                  refreshUserData={refreshUserData}
                />
              )}

              {activeTab === 'users' && canSeeUsersTab(user) && (
                <Users
                  user={user}
                  filteredUsers={filteredUsers}
                  userSearchTerm={userSearchTerm}
                  handleUserSearchChange={handleUserSearchChange}
                  userStatusFilter={userStatusFilter}
                  handleUserStatusFilterChange={handleUserStatusFilterChange}
                  showUserDropdown={showUserDropdown}
                  setShowUserDropdown={setShowUserDropdown}
                  handleToggle2FA={() => {}}
                  handleDeleteUser={handleDeleteUserWrapper}
                  handleEditUser={refreshUsers}
                  handleUserStatusChange={handleUserStatusChange}
                  userIntakeCompletions={userIntakeCompletions}
                  onUserClick={(userId: string) => {
                    handleFilterByUser(userId);
                    handleTabChange('projects');
                  }}
                  userCurrentPage={userCurrentPage}
                  setUserCurrentPage={setUserCurrentPage}
                  usersPerPage={usersPerPage}
                  totalUsers={filteredUsers.length}
                />
              )}

              {activeTab === 'organizations' && canSeeOrganizationsTab(user) && (
                <Organizations user={user} />
              )}

              {activeTab === 'programs' && canSeeProgramsTab(user) && (
                <Programs user={user} />
              )}

              {activeTab === 'groups' && canSeeGroupsTab(user) && (
                <Groups user={user} />
              )}


              {activeTab === 'projects' && (
                <Patients
                  user={user}
                  filteredPatients={filteredPatients}
                  isPatientLoading={isPatientLoading}
                  patientManagementSearchTerm={patientManagementSearchTerm}
                  handlePatientManagementSearch={handlePatientManagementSearch}
                  showPatientMenu={showPatientMenu}
                  setShowPatientMenu={setShowPatientMenu}
                  handleDeletePatient={handleDeletePatientWrapper}
                  patientStatusFilter={patientStatusFilter}
                  handlePatientStatusFilterChange={handlePatientStatusFilterChange}
                  handlePatientStatusChange={handlePatientStatusChange}
                  PatientNameDisplay={PatientNameDisplay}
                  PatientNotesDisplay={PatientNotesEditable}
                  formatDate={formatDate}
                  refreshPatients={refreshPatients}
                  handlePatientSelect={handlePatientSelect}
                  patientCurrentPage={patientCurrentPage}
                  setPatientCurrentPage={setPatientCurrentPage}
                  patientsPerPage={patientsPerPage}
                  totalPatients={filteredPatients.length}
                />
              )}

              {activeTab === 'forms' && (
                <>
                  {canSeeFormsTab(user) ? (
                    <FormManager user={user} searchTerm={globalSearchTerm} />
                  ) : (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
                      <h3 style={{ color: '#000000', marginBottom: '8px' }}>{t('formBuilder.noPermission')}</h3>
                      <p style={{ color: '#666' }}>{t('formBuilder.contactAdmin')}</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'calendar' && (
                <CalendarSection
                  user={user}
                  events={events}
                  calendarViewMode={calendarViewMode}
                  setCalendarViewMode={setCalendarViewMode}
                  dateRangeFilter={dateRangeFilter}
                  setDateRangeFilter={setDateRangeFilter}
                  eventSearchTerm={eventSearchTerm}
                  setEventSearchTerm={(term) => {
                    setGlobalSearchTerm(term);
                    setEventSearchTerm(term);
                  }}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  eventsPerPage={eventsPerPage}
                  currentDate={currentDate}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  setShowCreateEventModal={setShowCreateEventModal}
                  getEventsForDate={getEventsForDate}
                  getCalendarDays={getCalendarDays}
                  navigateMonth={navigateMonth}
                  switchToListView={switchToListView}
                  openEventDetailsModal={openEventDetailsModal}
                  handleEventStatusChange={handleEventStatusChange}
                  handleSyncSingleEvent={handleSyncSingleEvent}
                  handleDeleteEvent={handleDeleteEvent}
                  getFilteredEvents={getFilteredEvents}
                  getPaginatedEvents={getPaginatedEvents}
                />
              )}

              {activeTab === 'tasks' && (
                <TasksSection
                  user={user}
                  todayTasks={todayTasks}
                  pendingTasks={pendingTasks}
                  allTasks={allTasks}
                  isTasksLoading={isTasksLoading}
                  handleTaskStatusChange={handleTaskStatusChange}
                  handleDeleteTask={handleDeleteTask}
                  handlePatientSelect={handlePatientSelect}
                  refreshTasks={refreshTasks}
                />
              )}

              {/* Blocked User Popup */}
              {showBlockedPopup && (
                <BlockedUserPopup
                  user={user}
                  onSignOut={() => handleSignOut(true)}
                />
              )}

              {/* Patient Search Modal for Event Creation */}
              {showCreateEventModal && (
                <PatientSearchModal
                  onClose={() => setShowCreateEventModal(false)}
                  onSelect={handleSelectPatientForEvent}
                />
              )}

              {/* Event Details Modal */}
              {showEventDetailsModal && selectedEventForDetails && (
                <EventDetailsModal
                  selectedEvent={selectedEventForDetails}
                  user={user}
                  onClose={() => setShowEventDetailsModal(false)}
                  onDelete={handleDeleteEvent}
                  onUpdate={setEvents}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <DialogComponent />
    </>
  );
}
